/**
 * ActionsMenu Component
 * 
 * A dropdown menu button that provides quick access to features like:
 * - Create Research Scope (Google NotebookLM style)
 * - View/Edit existing Research Scopes
 * - Create Document Workspace
 * - View/Edit existing Document Workspaces
 * - Deep Research (web research)
 * - Other future actions
 * 
 * Positioned next to the model selector in ChatControls.
 */

import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Spinner, SpinnerSize } from '@fluentui/react';
import styles from './ActionsMenu.module.css';
import controlStyles from '../ChatControls/ChatControls.module.css';
import { 
  MoreHorizontalRegular,
  BookSearchRegular,
  GlobeSearchRegular,
  AddCircleRegular,
  EditRegular,
  DeleteRegular,
  DocumentRegular,
  DocumentAddRegular,
  FolderOpenRegular,
  DocumentPdfRegular,
  DocumentTextRegular,
  SlideTextRegular,
  PinRegular,
  ClipboardTaskListLtrRegular
} from '@fluentui/react-icons';
import { getResearchScopes, ResearchScope, deleteResearchScope } from '../../api/researchScopeApi';
import { listWorkspaces, DocumentWorkspace, deleteWorkspace } from '../../api/documentWorkspaceApi';

// Cache for research scopes and document workspaces (TTL: 60 seconds)
const CACHE_TTL_MS = 60000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class ActionsMenuCache {
  private scopesCache: CacheEntry<ResearchScope[]> | null = null;
  private workspacesCache: CacheEntry<DocumentWorkspace[]> | null = null;

  getScopes(): ResearchScope[] | null {
    if (this.scopesCache && Date.now() - this.scopesCache.timestamp < CACHE_TTL_MS) {
      return this.scopesCache.data;
    }
    return null;
  }

  setScopes(scopes: ResearchScope[]): void {
    this.scopesCache = { data: scopes, timestamp: Date.now() };
  }

  getWorkspaces(): DocumentWorkspace[] | null {
    if (this.workspacesCache && Date.now() - this.workspacesCache.timestamp < CACHE_TTL_MS) {
      return this.workspacesCache.data;
    }
    return null;
  }

  setWorkspaces(workspaces: DocumentWorkspace[]): void {
    this.workspacesCache = { data: workspaces, timestamp: Date.now() };
  }

  invalidateScopes(): void {
    this.scopesCache = null;
  }

  invalidateWorkspaces(): void {
    this.workspacesCache = null;
  }

  invalidateAll(): void {
    this.scopesCache = null;
    this.workspacesCache = null;
  }
}

// Singleton cache instance - exported for external invalidation
export const actionsMenuCache = new ActionsMenuCache();

interface ActionMenuItem {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  experimental?: boolean;
}

interface ActionsMenuProps {
  onCreateResearchScope: () => void;
  onEditResearchScope?: (scopeId: string) => void;
  onDeleteResearchScope?: (scopeId: string) => void;
  onDeepResearch?: () => void;
  onCreateDocumentWorkspace?: () => void;
  onEditDocumentWorkspace?: (workspaceId: string) => void;
  onDeleteDocumentWorkspace?: (workspaceId: string) => void;
  onPinDocumentWorkspace?: (workspaceId: string) => void;
  pinnedWorkspaceId?: string | null;
  disabled?: boolean;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  onCreateResearchScope,
  onEditResearchScope,
  onDeleteResearchScope,
  onDeepResearch,
  onCreateDocumentWorkspace,
  onEditDocumentWorkspace,
  onDeleteDocumentWorkspace,
  onPinDocumentWorkspace,
  pinnedWorkspaceId,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [researchScopes, setResearchScopes] = useState<ResearchScope[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Delete handlers with confirmation
  const handleDeleteScope = async (e: React.MouseEvent, scopeId: string, scopeName: string) => {
    e.stopPropagation();
    if (!confirm(`Delete research scope "${scopeName}"? This cannot be undone.`)) return;
    
    setDeletingId(scopeId);
    try {
      await deleteResearchScope(scopeId);
      actionsMenuCache.invalidateScopes();
      setResearchScopes(prev => prev.filter(s => s.id !== scopeId));
      onDeleteResearchScope?.(scopeId);
    } catch (err) {
      console.error('Failed to delete research scope:', err);
      alert('Failed to delete research scope');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteWorkspace = async (e: React.MouseEvent, workspaceId: string, title: string) => {
    e.stopPropagation();
    if (!confirm(`Delete document "${title}"? This cannot be undone.`)) return;
    
    setDeletingId(workspaceId);
    try {
      await deleteWorkspace(workspaceId);
      actionsMenuCache.invalidateWorkspaces();
      setDocumentWorkspaces(prev => prev.filter(w => w.id !== workspaceId));
      onDeleteDocumentWorkspace?.(workspaceId);
    } catch (err) {
      console.error('Failed to delete workspace:', err);
      alert('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };
  const [documentWorkspaces, setDocumentWorkspaces] = useState<DocumentWorkspace[]>([]);
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load research scopes and document workspaces when menu opens (use cache)
  useEffect(() => {
    if (isOpen) {
      loadResearchScopes();
      loadDocumentWorkspaces();
    }
  }, [isOpen]);

  const loadResearchScopes = async () => {
    // Check cache first
    const cached = actionsMenuCache.getScopes();
    if (cached) {
      setResearchScopes(cached);
      return;
    }
    
    setIsLoadingScopes(true);
    try {
      const scopes = await getResearchScopes(true);
      setResearchScopes(scopes);
      actionsMenuCache.setScopes(scopes);
    } catch (error) {
      console.error('Failed to load research scopes:', error);
    } finally {
      setIsLoadingScopes(false);
    }
  };

  const loadDocumentWorkspaces = async () => {
    // Check cache first
    const cached = actionsMenuCache.getWorkspaces();
    if (cached) {
      setDocumentWorkspaces(cached);
      return;
    }
    
    setIsLoadingWorkspaces(true);
    try {
      const workspaces = await listWorkspaces(10);
      setDocumentWorkspaces(workspaces);
      actionsMenuCache.setWorkspaces(workspaces);
    } catch (error) {
      console.error('Failed to load document workspaces:', error);
    } finally {
      setIsLoadingWorkspaces(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const menuItems: ActionMenuItem[] = [
    {
      id: 'create-research-scope',
      label: 'New Research Scope',
      description: 'Create a personal document collection for AI-powered research',
      icon: <BookSearchRegular style={{ fontSize: '18px' }} />,
      experimental: true,
      onClick: () => {
        setIsOpen(false);
        onCreateResearchScope();
      }
    }
  ];

  // Add Document Workspace if handler provided
  if (onCreateDocumentWorkspace) {
    menuItems.push({
      id: 'create-document-workspace',
      label: 'New Document Workspace',
      description: 'Create a document with AI using reference materials',
      icon: <DocumentAddRegular style={{ fontSize: '18px' }} />,
      experimental: true,
      onClick: () => {
        setIsOpen(false);
        onCreateDocumentWorkspace();
      }
    });
  }

  // Add Deep Research if handler provided
  if (onDeepResearch) {
    menuItems.push({
      id: 'deep-research',
      label: 'Deep Research',
      description: 'Comprehensive web research on a topic',
      icon: <GlobeSearchRegular style={{ fontSize: '18px' }} />,
      onClick: () => {
        setIsOpen(false);
        onDeepResearch();
      }
    });
  }

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <IconButton
        title="Actions Menu - Create research scopes, start deep research, and more"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`${controlStyles.controlButton} ${isOpen ? controlStyles.controlButtonActive : ''}`}
        styles={{
          root: { minWidth: '40px' },
          rootHovered: { 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)',
            borderColor: 'rgba(102, 126, 234, 0.4)',
            color: '#667eea'
          },
          rootDisabled: {
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            color: '#bdbdbd'
          }
        }}
      >
        <AddCircleRegular style={{ fontSize: '16px' }} />
      </IconButton>

      {isOpen && (
        <div
          className={styles.menuContainer}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.03) 100%)'
            }}
          >
            <div style={{ 
              fontWeight: 600, 
              fontSize: '13px', 
              color: '#667eea',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <MoreHorizontalRegular style={{ fontSize: '16px' }} />
              Actions
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ padding: '8px 6px' }}>
            {menuItems.map((item, index) => (
              <div
                key={item.id}
                onClick={item.disabled ? undefined : item.onClick}
                style={{
                  padding: '12px 14px',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: 'transparent',
                  opacity: item.disabled ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  borderRadius: '10px',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (!item.disabled) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.06) 100%)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px' 
                }}>
                  <div style={{ 
                    color: item.disabled ? '#bdbdbd' : '#667eea',
                    marginTop: '2px'
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ 
                      fontWeight: 500, 
                      fontSize: '14px', 
                      color: item.disabled ? '#9e9e9e' : '#374151',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {item.label}
                      {item.experimental && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: '#d97706',
                          background: 'rgba(217, 119, 6, 0.1)',
                          border: '1px solid rgba(217, 119, 6, 0.3)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Experimental
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280',
                      lineHeight: 1.4
                    }}>
                      {item.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Combined Workspaces Section */}
          {(onEditResearchScope || onEditDocumentWorkspace) && (
            <>
              <div
                style={{
                  padding: '10px 16px',
                  borderTop: '1px solid rgba(102, 126, 234, 0.1)',
                  borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.03) 100%)'
                }}
              >
                <div style={{ 
                  fontWeight: 600, 
                  fontSize: '12px', 
                  color: '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <FolderOpenRegular style={{ fontSize: '14px' }} />
                  Your Workspaces
                </div>
              </div>

              <div className={styles.workspacesList}>
                {(isLoadingScopes || isLoadingWorkspaces) ? (
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <Spinner size={SpinnerSize.small} />
                  </div>
                ) : (researchScopes.length === 0 && documentWorkspaces.length === 0) ? (
                  <div style={{ 
                    padding: '16px', 
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '13px'
                  }}>
                    No workspaces yet
                  </div>
                ) : (
                  <>
                    {/* Research Scopes */}
                    {onEditResearchScope && researchScopes.map((scope) => (
                      <div
                        key={`scope-${scope.id}`}
                        onClick={() => {
                          setIsOpen(false);
                          onEditResearchScope(scope.id);
                        }}
                        style={{
                          padding: '10px 14px',
                          margin: '2px 6px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          transition: 'all 0.2s ease',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.06) 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <BookSearchRegular style={{ fontSize: '16px', color: '#667eea' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: 500, 
                            fontSize: '13px', 
                            color: '#374151',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {scope.name}
                          </div>
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#6b7280'
                          }}>
                            Research Scope • {scope.file_count} {scope.file_count === 1 ? 'file' : 'files'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <EditRegular style={{ fontSize: '14px', color: '#94a3b8' }} />
                          <div
                            onClick={(e) => handleDeleteScope(e, scope.id, scope.name)}
                            title="Delete"
                            style={{
                              padding: '4px',
                              borderRadius: '6px',
                              cursor: deletingId === scope.id ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: 'transparent',
                              transition: 'all 0.2s ease',
                              opacity: deletingId === scope.id ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            {deletingId === scope.id ? (
                              <Spinner size={SpinnerSize.xSmall} />
                            ) : (
                              <DeleteRegular style={{ fontSize: '14px', color: '#ef4444' }} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Document Workspaces */}
                    {onEditDocumentWorkspace && documentWorkspaces.map((workspace) => (
                      <div
                        key={`workspace-${workspace.id}`}
                        onClick={() => {
                          setIsOpen(false);
                          onEditDocumentWorkspace(workspace.id);
                        }}
                        style={{
                          padding: '10px 14px',
                          margin: '2px 6px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          transition: 'all 0.2s ease',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.06) 100%)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        {workspace.workspaceMode === 'tender' ? (
                          <ClipboardTaskListLtrRegular style={{ fontSize: '16px', color: '#7c3aed' }} />
                        ) : workspace.format === 'pdf' ? (
                          <DocumentPdfRegular style={{ fontSize: '16px', color: '#e74c3c' }} />
                        ) : workspace.format === 'pptx' ? (
                          <SlideTextRegular style={{ fontSize: '16px', color: '#d35400' }} />
                        ) : (
                          <DocumentTextRegular style={{ fontSize: '16px', color: '#2980b9' }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: 500, 
                            fontSize: '13px', 
                            color: '#374151',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            {workspace.title}
                            {workspace.workspaceMode === 'tender' && (
                              <span style={{
                                fontSize: '9px',
                                fontWeight: 600,
                                color: '#7c3aed',
                                background: 'rgba(124, 58, 237, 0.1)',
                                border: '1px solid rgba(124, 58, 237, 0.3)',
                                padding: '2px 5px',
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}>Tender</span>
                            )}
                          </div>
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#6b7280'
                          }}>
                            {workspace.workspaceMode === 'tender' 
                              ? 'Tender Response' 
                              : (workspace.format === 'pdf' ? 'PDF' : workspace.format === 'pptx' ? 'PowerPoint' : 'Word')} • {workspace.status}
                          </div>
                        </div>
                        {onPinDocumentWorkspace && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              onPinDocumentWorkspace(workspace.id);
                              setIsOpen(false);
                            }}
                            title={pinnedWorkspaceId === workspace.id ? 'Pinned to chat' : 'Pin to chat'}
                            style={{
                              padding: '4px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: 'transparent',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <PinRegular style={{ 
                              fontSize: '14px', 
                              color: pinnedWorkspaceId === workspace.id ? '#667eea' : '#94a3b8' 
                            }} />
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <EditRegular style={{ fontSize: '14px', color: '#94a3b8' }} />
                          <div
                            onClick={(e) => handleDeleteWorkspace(e, workspace.id, workspace.title)}
                            title="Delete"
                            style={{
                              padding: '4px',
                              borderRadius: '6px',
                              cursor: deletingId === workspace.id ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: 'transparent',
                              transition: 'all 0.2s ease',
                              opacity: deletingId === workspace.id ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            {deletingId === workspace.id ? (
                              <Spinner size={SpinnerSize.xSmall} />
                            ) : (
                              <DeleteRegular style={{ fontSize: '14px', color: '#ef4444' }} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionsMenu;
